/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, useNavigate } from "react-router-dom";

// Soft UI Dashboard PRO React context
import { useSoftUIController } from "context";

// prop-types is a library for typechecking of props

import Card from "@mui/material/Card";

import { styled } from "@mui/system";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";

import { postData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

// Soft UI Dashboard PRO React routes
import routes from "routes";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import BetStats from "./betStats";

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

function LandingPage() {
  const abortController = new AbortController();
  const [userPermissions, setUserPermissions] = useState([]);
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor, permissions } =
    controller;
  const { pathname } = useLocation();
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [adminRole, setAdminRole] = useState(false);
  const [aborting, setAborting] = useState(false);
  const [totalBetsChartData, setTotalBetsChartData] = useState({
    totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
    labels: [],
    datasets: [
      {
        label: "Total Bets",
        color: "dark",
        data: [],
      },
    ],
  });
  const [totalBetOddsChartData, setTotalBetOddsChartData] = useState({
    totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
    labels: [],
    datasets: [
      {
        label: "Total Bet Odds",
        color: "dark",
        data: [],
      },
    ],
  });

  const [totalBetsChartDataToday, setTotalBetsChartDataToday] = useState({
    totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
    labels: [],
    datasets: [
      {
        label: "Total Bets",
        color: "dark",
        data: [],
      },
    ],
  });
  const [totalBetOddsChartDataToday, setTotalBetOddsChartDataToday] = useState({
    totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
    labels: [],
    datasets: [
      {
        label: "Total Bet Odds",
        color: "dark",
        data: [],
      },
    ],
  });

  const validUris = {
    "/feed-settings/odds": "/feed-settings/odds/odds-settings-list",
    "/feed-settings/markets": "/feed-settings/markets/markets-settings",
    "/feed-settings/filters": "/feed-settings/filters",
    "/feed-settings/templates": "/feed-settings/templates",
    "/management/translations": "/management/translations/sports",
    "/settlement": "/settlement/advanced",
    "/management": "/management/platforms",
  };

  let navigate = useNavigate();
  const routeChange = (uri) => {
    let path = `/dashboard`;
    let validUri = checkValidUri(uri);
    if (validUri) path = validUri;
    navigate(path);
    return;
  };

  const checkValidUri = (uri) => {
    let redirection = validUris[uri];

    return redirection;
  };

  const checkPermission = (permission) => {
    // console.log("permission: ", permission);
    // console.log("userPermissions: ", userPermissions);

    if (permission === true) return true;
    if (permission === false) return false;
    if (userPermissions.includes("super")) return true;
    return userPermissions.includes(permission);
  };

  const filterRoutes = (allRoutes) => {
    // console.log("all routes : ", routes);
    // console.log("filtering routes : ", allRoutes);
    return allRoutes.filter((route) => {
      if (checkPermission(route.permission)) {
        if (route.collapse) {
          route.collapse = route.collapse.filter((routeCollapse) => {
            if (checkPermission(routeCollapse.permission)) return routeCollapse;
          });

          route.collapse.forEach((leaf) => {
            if (leaf.collapse) {
              leaf.collapse = leaf.collapse.filter((leafCollapse) => {
                if (checkPermission(leafCollapse.permission)) return leafCollapse;
              });
            }
          });
        }
        return route;
      }
    });
  };

  async function fetchStats() {
    // await fetchMonthlyStats();
    // await fetchTodayStats();
    setStatsLoaded(true);
  }

  async function fetchMonthlyStats() {
    if (aborting) return;

    let getOddsEndpoint = `/api/v1/bets/stats`;
    let response = await postData(
      getOddsEndpoint,
      {},
      {
        Authorization: getAuthorizationHeader(),
      },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
      let totalBetsChartData = {
        totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
        labels: [],
        datasets: [
          {
            label: "Total Bets",
            color: "dark",
            data: [],
            tableData: [],
          },
        ],
      };
      let totalBetOddsChartData = {
        totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
        labels: [],
        datasets: [
          {
            label: "Total Bet Odds",
            colors: "dark",
            data: [],
            tableData: [],
          },
        ],
      };

      for (let index = 0; index < response.data.length; index++) {
        const platformNUmbers = response.data[index];
        totalBetsChartData.labels.push(platformNUmbers.platform_name);
        totalBetsChartData.datasets[0].tableData.push(platformNUmbers);
        totalBetsChartData.datasets[0].data.push(platformNUmbers.total_bets);
        totalBetsChartData.totals[0].all =
          totalBetsChartData.totals[0].all + platformNUmbers.total_bets * 1;
        totalBetOddsChartData.labels.push(platformNUmbers.platform_name);
        totalBetOddsChartData.datasets[0].tableData.push(platformNUmbers);
        totalBetOddsChartData.datasets[0].data.push(platformNUmbers.total_bet_odds);
        totalBetOddsChartData.totals[0].all =
          totalBetOddsChartData.totals[0].all + platformNUmbers.total_bet_odds * 1;
        totalBetOddsChartData.totals[0].won =
          totalBetOddsChartData.totals[0].won + platformNUmbers.total_won * 1;
        totalBetOddsChartData.totals[0].lost =
          totalBetOddsChartData.totals[0].lost + platformNUmbers.total_lost * 1;
        totalBetOddsChartData.totals[0].unsettled =
          totalBetOddsChartData.totals[0].unsettled + platformNUmbers.total_unsettled * 1;
        totalBetOddsChartData.totals[0].settled =
          totalBetOddsChartData.totals[0].settled + platformNUmbers.total_settled * 1;
        totalBetOddsChartData.totals[0].refund =
          totalBetOddsChartData.totals[0].refund + platformNUmbers.total_refund * 1;
      }

      setTotalBetsChartData(totalBetsChartData);
      setTotalBetOddsChartData(totalBetOddsChartData);
    }
  }

  async function fetchTodayStats() {
    if (aborting) return;

    let getOddsEndpoint = `/api/v1/bets/stats`;
    let today = new Date();
    today.setHours(0, 0, 0, 0);
    let day = new Date(today);
    day.setHours(0, 0, 0, 0);
    day.setDate(today.getDate() + 1);
    let dt_from = today;
    let dt_to = day;
    let response = await postData(
      getOddsEndpoint,
      { bet_from_date: dt_from, bet_to_date: dt_to },
      {
        Authorization: getAuthorizationHeader(),
      },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
      let totalBetsChartDataToday = {
        totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
        labels: [],
        datasets: [
          {
            label: "Total Bets",
            colors: [],
            data: [],
            tableData: [],
          },
        ],
      };
      let totalBetOddsChartDataToday = {
        totals: [{ all: 0, won: 0, lost: 0, refund: 0, unsettled: 0, settled: 0 }],
        labels: [],
        datasets: [
          {
            label: "Total Bet Odds",
            colors: [],
            data: [],
            tableData: [],
          },
        ],
      };

      for (let index = 0; index < response.data.length; index++) {
        const platformNUmbers = response.data[index];
        totalBetsChartDataToday.labels.push(platformNUmbers.platform_name);
        totalBetsChartDataToday.datasets[0].tableData.push(platformNUmbers);
        totalBetsChartDataToday.datasets[0].data.push(platformNUmbers.total_bets);
        totalBetsChartDataToday.datasets[0].colors.push("info"); //"info", "primary", "dark", "secondary", "primary"
        totalBetsChartDataToday.totals[0].all =
          totalBetsChartDataToday.totals[0].all + platformNUmbers.total_bets * 1;
        totalBetOddsChartDataToday.labels.push(platformNUmbers.platform_name);
        totalBetOddsChartDataToday.datasets[0].tableData.push(platformNUmbers);
        totalBetOddsChartDataToday.datasets[0].data.push(platformNUmbers.total_bet_odds);
        totalBetOddsChartDataToday.datasets[0].colors.push("primary");
        totalBetOddsChartDataToday.totals[0].all =
          totalBetOddsChartDataToday.totals[0].all + platformNUmbers.total_bet_odds * 1;
        totalBetOddsChartDataToday.totals[0].won =
          totalBetOddsChartDataToday.totals[0].won + platformNUmbers.total_won * 1;
        totalBetOddsChartDataToday.totals[0].lost =
          totalBetOddsChartDataToday.totals[0].lost + platformNUmbers.total_lost * 1;
        totalBetOddsChartDataToday.totals[0].unsettled =
          totalBetOddsChartDataToday.totals[0].unsettled + platformNUmbers.total_unsettled * 1;
        totalBetOddsChartDataToday.totals[0].settled =
          totalBetOddsChartDataToday.totals[0].settled + platformNUmbers.total_settled * 1;
        totalBetOddsChartDataToday.totals[0].refund =
          totalBetOddsChartDataToday.totals[0].refund + platformNUmbers.total_refund * 1;
      }
      setTotalBetsChartDataToday(totalBetsChartDataToday);
      setTotalBetOddsChartDataToday(totalBetOddsChartDataToday);
    }
  }

  function getRandomColor() {
    var letters = "0123456789ABCDEF";
    var color = "#";
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  useEffect(() => {
    //console.log("location:", pathname);
    if (permissions.length == 0) return;
    if (pathname !== "/dashboard") {
      let filteredRoutes = filterRoutes(routes);
      //console.log("Landing Page: ", pathname);
      routeChange(pathname);
    }
  }, [pathname, permissions]);

  useEffect(() => {
    // console.log("permissions: ", permissions);
    if (permissions.length == 0) return;
    let userPermissions = [...permissions];

    setUserPermissions(userPermissions);
    const userData = getUserData();
    let adminRole = userData && userData.roles.find((item) => item.id == 1);
    if (adminRole) {
      setAdminRole(true);
      if (!aborting) fetchStats();
    } else {
      setAdminRole(true);
      if (!aborting) fetchStats();
    }
  }, [permissions]);

  useEffect(() => {
    setAborting(false);
    return () => {
      // console.log("living page...");
      if (abortController) {
        // console.log("abortController landing page");
        setAborting(true);
        abortController.abort();
      }
    };
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        my={3}
        style={{ display: permissions.length > 0 ? (adminRole ? "none" : "") : "none" }}
      >
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Welcome to &ldquo;Feed the Bet&rdquo; Backoffice.
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                All tools you need to calibrate the feed you want to get is in this panel.
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
      <SoftBox mb={3} sx={{ display: adminRole ? (statsLoaded ? "" : "none") : "none" }}>
        <BetStats
          data={{
            title: "Today's Stats",
            totalBetsChartData: totalBetsChartDataToday,
            totalBetOddsChartData: totalBetOddsChartDataToday,
          }}
        ></BetStats>
        <hr className="hr-divider" />

        <BetStats
          data={{
            title: "Current Month Stats",
            totalBetsChartData: totalBetsChartData,
            totalBetOddsChartData: totalBetOddsChartData,
          }}
        ></BetStats>
      </SoftBox>
    </DashboardLayout>
  );
}

// Typechecking props for the DataTable
LandingPage.propTypes = {};

export default LandingPage;
